/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description STRListItemContainer
 */

import { type WithOptimizelyProps, withOptimizely } from '@optimizely/react-sdk'
import { selectFilters } from '@zcode-ui/filters'
import { selectHasInitialLocationChanged, selectIsBot } from '@zcode-ui/web-zumper-global-state'
import { QUICK_LOOK_CLICK } from '@zcode-ui/web-zumper-integrations'
import { PROP_NAMES } from '@zcode/mixpanel'
import { Origin, isGalleryDirect, localStorageSingleton } from '@zcode/nr-utils'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { QUICK_LOOK_CLICK as OPTIMIZELY_QUICK_LOOK_CLICK } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import { hocCompose, withErrorBoundary } from '@zcode/react-utils'
import { routerConnect } from '@zcode/router-connect'
import type { RouteComponentProps } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import { rootRelative } from '@zcode/utils'
import React, { type KeyboardEvent, type MouseEvent } from 'react'
import { type InferableComponentEnhancerWithProps, connect } from 'react-redux'
import { setHoveredItem } from '../../../../../actions/index'
import { getVerticalsArray, selectIsCurrentSearchView } from '../../../../../inspectors/index'
import { selectCurrentSearchRoutePathname } from '../../../../../modules/currentSearch/selectors'
import { openFullDetailTab } from '../../../../../modules/detail/thunks/openFullDetailTab'
import { trackListItemImpression } from '../../../../../modules/listItem/thunks/index'
import { mixpanelMaplistClick } from '../../../../../modules/mixpanel/thunks/mixpanelMaplistClick'
import { selectPage } from '../../../../../modules/router/selectors/index'
import { trackListCardClick } from '../../../../../modules/tracking/thunks/trackListCardClickout'
import { trackShortTermClickout } from '../../../../../modules/tracking/thunks/trackShortTermClickout'
import {
  CURRENT_SEARCH_VIEW,
  DETAIL_SIMILAR,
  type FAVORITES,
  type MESSAGED,
  type NEARBY,
  type SAVED_SEARCHES,
} from '../../../../../modules/traktor/constants/index'
import { trackListItemEvent } from '../../../../../modules/traktor/thunks/trackListItemEvent'
import type { Dispatch } from '../../../../../modules/types'
import { getFeedName } from '../../../../detail/helpers/getFeedName'
import { SpotlightListingCard } from '../SpotlightListingCard/SpotlightListingCard'
import { SpotlightListingCardLarge } from '../SpotlightListingCardLarge/SpotlightListingCardLarge'
import { RailListingCard, type RailListingCardProps } from './RailListingCard'
import { STRListItem, type STRListItemProps } from './STRListItem'
import { getProviderHref } from './helpers/getProviderHref'

interface STRListItemContainerProps
  extends Pick<STRListItemProps & RailListingCardProps, 'entity' | 'index' | 'page'> {
  listName?:
    | typeof MESSAGED
    | typeof NEARBY
    | typeof CURRENT_SEARCH_VIEW
    | typeof DETAIL_SIMILAR
    | typeof SAVED_SEARCHES
    | typeof FAVORITES
  onClickItem?: () => void
}

export const mapRouteToProps = (routeProps: RouteComponentProps) => {
  return {
    isCurrentSearchView: selectIsCurrentSearchView(routeProps),
    verticalsArray: getVerticalsArray(routeProps),
    page: selectPage(routeProps),
  }
}

export const mapStateToProps = (state: State, ownProps: STRListItemContainerProps) => {
  const {
    entity: { externalUrl },
  } = ownProps
  const filters = selectFilters(state)
  const isShortTerm = !filters.longTerm
  return {
    bookingProviderHref: getProviderHref({ externalUrl, filters }),
    isInitialLocationChanged: selectHasInitialLocationChanged(state),
    isShortTerm,
  }
}

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: STRListItemContainerProps & WithOptimizelyProps
) => {
  const { entity, listName, index, onClickItem, page } = ownProps

  return {
    onClick: (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
      event.preventDefault()
      onClickItem?.()
      dispatch((_, getState) => {
        const state = getState()
        const filters = selectFilters(state)
        const pathname = selectCurrentSearchRoutePathname(state)
        const isBot = selectIsBot(state)
        const { location } = dispatch(getRouteProps())
        const isGalleryDirectUx = isGalleryDirect({
          isBot,
          location,
        })
        const isGalleryLanding = (entity.hasImage || entity.hasMedia) && isGalleryDirectUx

        dispatch(
          openFullDetailTab({
            entity,
            filters,
            isGalleryURL: isGalleryLanding,
          })
        )
        // do not block main thread by calling this in a timeout
        setTimeout(() => {
          localStorageSingleton.set('mapListFilters', { ...filters, url: pathname })
        }, 1)
      })
      const additionalProps = { railLocation: index, isFilterAd: entity?.isFilterAd }
      dispatch(trackListCardClick({ entity, additionalProps }))
    },
    onClickQuickLook: (event: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      const additionalProps = { railLocation: index, isFilterAd: entity?.isFilterAd }
      dispatch(trackListItemEvent({ name: QUICK_LOOK_CLICK, entity, additionalProps }))
      dispatch(Optimizely.track({ eventKey: OPTIMIZELY_QUICK_LOOK_CLICK }))
      dispatch(mixpanelMaplistClick({ buttonClicked: 'Quick Look', entity }))
    },
    onClickThirdPartyListCard: () => {
      dispatch((_, getState) => {
        const state = getState()
        const filters = selectFilters(state)
        const { externalUrl } = entity
        const bookingProviderHref = getProviderHref({ externalUrl, filters })
        const feedName = getFeedName(entity)
        const providerURL = bookingProviderHref || entity?.url

        if (providerURL) {
          const href = rootRelative(providerURL)
          window.open(
            `/provider-interstitial?provider=${feedName}&url=${href.replaceAll('&', '$')}`,
            '_blank'
          )
          dispatch(
            trackShortTermClickout({
              origin: Origin.LIST_ITEM,
              entity,
              extraPayload: { [PROP_NAMES.BUTTON_CLICKED]: 'List card' },
            })
          )
        } else {
          dispatch(openFullDetailTab({ entity, filters }))
        }
      })
    },
    onClickMoreAmenities: () => {
      dispatch(
        mixpanelMaplistClick({
          entity,
          buttonClicked: 'More Amenities',
          section: 'Listing Card',
        })
      )
    },
    onMouseEnter: () => {
      dispatch(setHoveredItem(entity))
    },
    onMouseLeave: () => {
      dispatch(setHoveredItem())
    },
    trackListItemImpression: () => {
      dispatch(trackListItemImpression({ entity, index, listName, page }))
    },
    onClickout: () => {
      dispatch(
        trackShortTermClickout({
          origin: Origin.LIST_ITEM,
          entity,
          extraPayload: { [PROP_NAMES.BUTTON_CLICKED]: 'CTA' },
        })
      )
    },
  }
}

const STRListItemContainerInnerContainer = (
  props: STRListItemProps & {
    forceSpotlightListingCard?: boolean
    forceSpotlightListingCardLarge?: boolean
    isCurrentSearchView: boolean
    listName?: string
    isSimilarListings?: boolean
  }
) => {
  const {
    forceSpotlightListingCard,
    forceSpotlightListingCardLarge,
    isCurrentSearchView,
    isSimilarListings,
    listName,
    entity,
    page,
  } = props
  const { isSpotlight } = entity

  if (
    forceSpotlightListingCard ||
    forceSpotlightListingCardLarge ||
    (((page === 1 && listName === CURRENT_SEARCH_VIEW) || listName === DETAIL_SIMILAR) &&
      isSpotlight)
  ) {
    if (forceSpotlightListingCardLarge) {
      return <SpotlightListingCardLarge {...props} isSimilarListings={isSimilarListings} />
    }

    return <SpotlightListingCard {...props} isSimilarListings={isSimilarListings} />
  }

  if (isCurrentSearchView || isSimilarListings) {
    return <RailListingCard {...props} isSimilarListings={isSimilarListings} />
  }
  return <STRListItem {...props} />
}

export const STRListItemContainer = hocCompose(
  withOptimizely as unknown as InferableComponentEnhancerWithProps<WithOptimizelyProps, {}>,
  routerConnect(mapRouteToProps),
  connect(mapStateToProps, mapDispatchToProps),
  withErrorBoundary({ displayName: 'STRListItem' }),
  STRListItemContainerInnerContainer
)
